import { useEffect, useState } from "react";
import { Badge, Button, Dropdown, Form, Spinner } from "react-bootstrap";

import {
  canApproveRegistration,
  canEditFiringOrder,
  canEditRallyNumber,
  canUnapproveRegistration,
  getDriverName,
  getEntityId,
  getFiringOrder,
  getInheritedConsentInfo,
  getRallyNumber,
  getTeamId,
  getTeamName,
  getTypeName,
  getCategoryTitle,
  isQualifiedRegistration,
} from "../utils/competitors.utils";

const InlineNumberInput = ({
  value,
  disabled,
  saving,
  error,
  placeholder,
  onSave,
}) => {
  const [draft, setDraft] = useState(value === 0 || value ? String(value) : "");

  useEffect(() => {
    setDraft(value === 0 || value ? String(value) : "");
  }, [value]);

  const commit = () => {
    if (disabled || saving) return;
    const next = draft.trim();
    const current = value === 0 || value ? String(value) : "";
    if (next === current) return;
    onSave(next === "" ? null : Number(next));
  };

  return (
    <div style={{ minWidth: 88 }}>
      <Form.Control
        type="number"
        min={1}
        step={1}
        size="sm"
        value={draft}
        disabled={disabled || saving}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        isInvalid={Boolean(error)}
      />
      {saving ? (
        <Spinner animation="border" size="sm" className="mt-1" />
      ) : null}
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
};

const InlineTextInput = ({
  value,
  disabled,
  saving,
  error,
  placeholder,
  onSave,
}) => {
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = () => {
    if (disabled || saving) return;
    const next = draft.trim();
    const current = String(value || "").trim();
    if (next === current) return;
    onSave(next);
  };

  return (
    <div style={{ minWidth: 100 }}>
      <Form.Control
        type="text"
        size="sm"
        value={draft}
        disabled={disabled || saving}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        isInvalid={Boolean(error)}
      />
      {saving ? (
        <Spinner animation="border" size="sm" className="mt-1" />
      ) : null}
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
};

const ConsentIcons = ({
  consent,
  uploading,
  canManage,
  onDownloadTemplate,
  onDownloadSigned,
  onUploadSigned,
}) => {
  return (
    <div className="d-flex align-items-center gap-2">
      <Button
        type="button"
        variant="link"
        className="p-0 text-decoration-none"
        title={
          consent.hasConsentTemplate
            ? "Download consent template"
            : "No consent template"
        }
        aria-label="Download consent template"
        disabled={!consent.hasConsentTemplate}
        onClick={onDownloadTemplate}
      >
        <i
          className={`bi bi-file-earmark-text fs-5 ${
            consent.hasConsentTemplate ? "text-primary" : "text-muted"
          }`}
        />
      </Button>

      <Button
        type="button"
        variant="link"
        className="p-0 text-decoration-none"
        title={
          consent.signedUrl
            ? "Download signed consent"
            : "No signed consent uploaded"
        }
        aria-label="Download signed consent"
        disabled={!consent.signedUrl}
        onClick={onDownloadSigned}
      >
        <i
          className={`bi bi-download fs-5 ${
            consent.signedUrl ? "text-success" : "text-muted"
          }`}
        />
      </Button>

      {canManage ? (
        <Button
          type="button"
          variant="link"
          className="p-0 text-decoration-none"
          title={
            consent.isSigned
              ? "Replace signed consent PDF"
              : "Upload signed consent PDF"
          }
          aria-label="Upload signed consent"
          disabled={!consent.categoryId || uploading}
          onClick={onUploadSigned}
        >
          {uploading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <i className="bi bi-upload fs-5 text-secondary" />
          )}
        </Button>
      ) : null}

      <Badge bg={consent.isSigned ? "success" : "secondary"} className="ms-1">
        {consent.isSigned ? "Signed" : "Unsigned"}
      </Badge>
    </div>
  );
};

const CompetitorsTable = ({
  registrations = [],
  categories = [],
  canManage = false,
  selectedIds = [],
  approvingId = "",
  uploadingCategoryId = "",
  savingFiringOrderId = "",
  savingRallyNumberId = "",
  fieldErrors = {},
  onToggleSelect,
  onToggleSelectAll,
  onApprove,
  onUnapprove,
  onView,
  onEdit,
  onOpenTeam,
  onOpenInspection,
  onSaveFiringOrder,
  onSaveRallyNumber,
  onDownloadConsent,
  onDownloadSignedConsent,
  onUploadSignedConsent,
}) => {
  const allIds = registrations.map((item) => getEntityId(item)).filter(Boolean);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  return (
    <div className="table-responsive">
      <table className="table table-striped mb-0 align-middle">
        <thead>
          <tr>
            <th style={{ width: 40 }}>
              <Form.Check
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={(e) => onToggleSelectAll(e.target.checked)}
                aria-label="Select all registrations"
              />
            </th>
            <th style={{ minWidth: 130 }}>Name</th>
            <th style={{ minWidth: 130 }}>Team name</th>
            <th style={{ minWidth: 110 }}>Type</th>
            <th style={{ minWidth: 120 }}>Category</th>
            <th style={{ minWidth: 170 }}>Consent form</th>
            <th style={{ width: 110 }}>Inspection</th>
            <th style={{ width: 100 }}>Qualified</th>
            <th style={{ width: 110 }}>Firing order</th>
            <th style={{ width: 120 }}>Rally no</th>
            <th className="text-center" style={{ width: 96 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {registrations.map((registration, index) => {
            const id = getEntityId(registration);
            const status = registration.status || "";
            const consent = getInheritedConsentInfo(registration, categories);
            const teamId = getTeamId(registration);
            const busy = Boolean(approvingId) && approvingId === id;
            const uploading =
              Boolean(uploadingCategoryId) &&
              uploadingCategoryId === consent.categoryId;
            const selected = Boolean(id) && selectedIds.includes(id);
            const qualified = isQualifiedRegistration(registration);
            const inspected = Boolean(
              registration.inspected ||
                registration.inspection ||
                getEntityId(registration.inspection)
            );
            const rowErrors = fieldErrors[id] || {};

            return (
              <tr key={id || `registration-${index}`}>
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selected}
                    disabled={!id}
                    onChange={() => onToggleSelect(id)}
                    aria-label={`Select ${getDriverName(registration)}`}
                  />
                </td>
                <td className="fw-semibold">{getDriverName(registration)}</td>
                <td>
                  {teamId ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline"
                      onClick={() => onOpenTeam(teamId, registration)}
                    >
                      {getTeamName(registration)}
                    </button>
                  ) : (
                    getTeamName(registration)
                  )}
                </td>
                <td>{getTypeName(registration)}</td>
                <td>{getCategoryTitle(registration)}</td>
                <td>
                  <ConsentIcons
                    consent={consent}
                    uploading={uploading}
                    canManage={canManage}
                    onDownloadTemplate={() => onDownloadConsent(registration)}
                    onDownloadSigned={() =>
                      onDownloadSignedConsent(consent.signedUrl)
                    }
                    onUploadSigned={() => onUploadSignedConsent(registration)}
                  />
                </td>
                <td>
                  <Button
                    type="button"
                    size="sm"
                    variant={inspected ? "outline-success" : "outline-primary"}
                    disabled={!id || !canManage}
                    onClick={() => onOpenInspection(registration)}
                  >
                    {inspected ? "Edit form" : "Open form"}
                  </Button>
                </td>
                <td>
                  <Badge bg={qualified ? "success" : "secondary"}>
                    {qualified ? "Qualified" : "Not qualified"}
                  </Badge>
                </td>
                <td>
                  <InlineNumberInput
                    value={getFiringOrder(registration)}
                    disabled={!canManage || !canEditFiringOrder(registration)}
                    saving={savingFiringOrderId === id}
                    error={rowErrors.firing_order}
                    placeholder="#"
                    onSave={(value) => onSaveFiringOrder(registration, value)}
                  />
                </td>
                <td>
                  <InlineTextInput
                    value={getRallyNumber(registration)}
                    disabled={!canManage || !canEditRallyNumber(registration)}
                    saving={savingRallyNumberId === id}
                    error={rowErrors.rally_number}
                    placeholder="Rally no"
                    onSave={(value) => onSaveRallyNumber(registration, value)}
                  />
                </td>
                <td className="text-center">
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="link"
                      className="p-0 text-decoration-none"
                    >
                      &#x2022;&#x2022;&#x2022;
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end">
                      <Dropdown.Item
                        onClick={() => onView(registration)}
                        disabled={!id}
                      >
                        View
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => onEdit(registration)}
                        disabled={!id || !canManage}
                      >
                        Edit
                      </Dropdown.Item>
                      {canApproveRegistration(status) ? (
                        <Dropdown.Item
                          onClick={() => onApprove(registration)}
                          disabled={!canManage || !id || busy}
                        >
                          {busy ? "Approving..." : "Approve"}
                        </Dropdown.Item>
                      ) : null}
                      {canUnapproveRegistration(status) ? (
                        <Dropdown.Item
                          onClick={() => onUnapprove(registration)}
                          disabled={!canManage || !id || busy}
                        >
                          {busy ? "Updating..." : "Set pending"}
                        </Dropdown.Item>
                      ) : null}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CompetitorsTable;
